import { useState, useEffect, ReactElement } from 'react';
import { 
  Sparkles, Download, AlertTriangle, CheckCircle, Home, 
  Lightbulb, Loader2, FileText, Target, BookOpen, 
  RefreshCw, Copy, Check, ChevronRight, Zap, Trash2
} from 'lucide-react';

// --- 1. Configuration ---
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

// STRATEGY: "Waterfall" Fallback
// 1. gemini-2.5-flash (Fastest/Newest - Matches your account)
// 2. gemini-2.0-flash-001 (Stable Backup)
// 3. gemini-2.0-flash (Alias)
const API_CONFIG = {
  models: ['gemini-2.5-flash', 'gemini-2.0-flash-001', 'gemini-2.0-flash'], 
  endpoint: 'https://generativelanguage.googleapis.com/v1beta/models/'
};

const EXAMPLE_PROMPTS = [
  "A 3-day youth basketball tournament for ages 12-16 in Melbourne, targeting 32 teams from Victoria, with skills clinics, college recruitment opportunities, and championship finals broadcast.",
  "A professional esports league with 8 teams competing over a 10-week season, including online qualifiers, studio finals, and a 50K prize pool.",
  "A community 5K fun run and family festival for 500+ participants to raise $25,000 for local youth sports, featuring food trucks, live music, and corporate sponsors."
];

// --- 2. FIXED Prompts with Proper Context Passing ---
const PROMPTS = {
  canvas: (input: string) => `
    Role: Senior Sport Event Strategist.
    Task: Create a Sport Event Canvas for: "${input}".
    
    Format Guide:
    - Tone: Professional, executive, strategic.
    - Structure: Use '### Headers' for sections.
    - Content: Use paragraphs for strategy, bullet points ONLY for lists.
    - NO TABLES (Use text blocks).
    
    Output Structure:
    # Sport Event Canvas

    ## 1. Purpose 🎯
    ### Primary Goal
    [Specific, measurable objective sentence for THIS event]
    ### The Why
    [Compelling emotional and practical reason for hosting THIS specific event]
    ### Success Vision
    [What success looks like for THIS event in one powerful sentence]

    ## 2. Stakeholders 👥
    ### Primary Stakeholders
    [Description of 3-5 key groups specific to THIS event and their needs]
    ### Secondary Stakeholders
    [Description of partners, community relevant to THIS event, and their expectations]

    ## 3. Experience ✨
    ### The Core Journey
    [Narrative of what participants in THIS specific event will feel, see, and do]
    ### Unique Elements
    [2-3 things that make THIS particular event unmissable]
    ### Emotional Impact
    [Target emotions we want to evoke in THIS event]

    ## 4. Plan & Timeline 🗓
    ### Planning Phase
    [Bullet list of milestones specific to THIS event type]
    ### Promotion Phase
    [Bullet list of marketing actions for THIS event]
    ### Execution (Event Day)
    [Bullet list of critical run-of-show items for THIS event]

    ## 5. Project Lead 👤
    ### Ownership
    [Role responsible for THIS event and key decision authority]
    ### Key Responsibilities
    [3-5 core duties for THIS specific event]

    ## 6. Resources ⚙️
    [Bullet list of Human, Physical, and Tech resources needed for THIS event]

    ## 7. Risk & Change ⚠️
    ### High-Impact Risks
    [List top 3 risks specific to THIS event type with brief mitigation strategies]

    ## 8. Success Metrics 📊
    ### Financial KPIs
    [Revenue and Profit targets realistic for THIS event]
    ### Operational KPIs
    [Participant numbers and satisfaction targets for THIS event]

    ## 9. Cost Structure 💸
    [List of Fixed and Variable cost categories relevant to THIS event type]

    ## 10. Revenue Streams 💰
    [List of Primary Revenue and Sponsorship tiers appropriate for THIS event]
    
    Be specific to the event described. Use realistic numbers and details for THIS particular event.
  `,
  
  boscard: (canvasContext: string) => `
    Role: Senior Project Manager.
    Task: Create a BOSCARD Strategic Alignment Document.
    
    CRITICAL INSTRUCTION: This BOSCARD must be for the EXACT SAME EVENT described in the Canvas below. 
    DO NOT create a different event. Use the specific details, goals, and context provided.
    
    === SPORT EVENT CANVAS (YOUR FOUNDATION) ===
    ${canvasContext}
    === END OF CANVAS ===
    
    Now create the BOSCARD that expands on THIS EXACT SAME EVENT from the Canvas above.
    
    Format Guide:
    - Professional Executive Tone.
    - Headers followed by Paragraphs.
    - Bullets for specific lists only.
    
    Output Structure:
    # BOSCARD Strategic Alignment

    ## 1. Background 🎯
    [3 Paragraphs covering: Why THIS SPECIFIC event exists (reference event type and goals from Canvas), 
    market need for THIS event, timing rationale, and alignment with organizational goals. 
    Use the same event description, target audience, and purpose from the Canvas above.]

    ## 2. Objectives (SMART) 📈
    ### Financial Objectives
    [Detailed paragraph on revenue targets, margins, and ROI - USE THE EXACT NUMBERS from Canvas Revenue Streams and Success Metrics]
    ### Operational Objectives
    [Detailed paragraph on attendance, quality standards, and safety - USE THE EXACT PARTICIPANT NUMBERS from Canvas Success Metrics]
    ### Strategic Objectives
    [Detailed paragraph on brand awareness, media reach, and legacy - REFERENCE the Strategic KPIs from Canvas]

    ## 3. Scope ✅
    ### In Scope
    [Comprehensive bullet list of 8-12 specific deliverables based on:
    - The Experience section from Canvas
    - The event type described in Canvas Purpose
    - The resources mentioned in Canvas Resources section]
    ### Out of Scope
    [Clear bullet list of 5-8 exclusions that make sense for THIS event type from Canvas]

    ## 4. Constraints ⚙️
    ### Budget & Resources
    [Paragraph details on financial limits - REFERENCE the budget from Canvas Cost Structure
    and team capacity from Canvas Resources]
    ### Time & Regulatory
    [Paragraph details on deadlines from Canvas Plan & Timeline, permits, and external dependencies 
    relevant to THIS event type]

    ## 5. Assumptions 🤔
    [Paragraphs detailing Market, Operational, and External assumptions specific to THIS event from Canvas]

    ## 6. Risks ⚠️
    [Executive summary of the EXACT 3 risks from Canvas Risk & Change section, expanded with mitigation philosophy]

    ## 7. Deliverables 📦
    [List of Pre-Event, Event-Day, and Post-Event deliverables organized by the phases 
    in Canvas Plan & Timeline section]
    
    CRITICAL: Maintain complete consistency with the event type, scale, goals, and all details from the Canvas above.
  `,
  
  blueprint: (canvasContext: string, boscardContext: string) => `
    Role: Operations Director.
    Task: Create a DETAILED Event Blueprint (The Execution Plan).
    
    CRITICAL INSTRUCTION: This Blueprint MUST be for the EXACT SAME EVENT described in the Canvas and BOSCARD below.
    DO NOT create a different event. Use the specific details, goals, and context provided in both documents.
    
    === SPORT EVENT CANVAS (FOUNDATION) ===
    ${canvasContext}
    === END OF CANVAS ===
    
    === BOSCARD STRATEGIC ALIGNMENT (CONTEXT) ===
    ${boscardContext}
    === END OF BOSCARD ===
    
    Now create a detailed Implementation Blueprint that expands on the SAME EVENT described in both documents above.
    
    Maintain complete consistency with:
    - Event type, name, and description from Canvas
    - Target audience and participant numbers from Canvas Success Metrics
    - Revenue and cost structures from Canvas Cost Structure and Revenue Streams
    - Success metrics and objectives from Canvas and BOSCARD
    - Timeline phases from Canvas Plan & Timeline
    - All stakeholder information from Canvas
    
    Format Guide:
    - This must follow the "Event Playbook" Framework: Vision → Plan → Reality Check
    - NO TABLES. Use Text Blocks.
    - Professional, executive tone
    
    Output Structure:
    # Sport Event Blueprint

    ## Executive Summary
    [3 paragraphs that:
    1. Describe the EXACT event from Canvas (same type, scale, audience, purpose)
    2. Reference the specific outcomes from Canvas Success Metrics and BOSCARD Objectives
    3. Explain key differentiators from Canvas Experience section]

    ## THE VISION
    ### Background & Purpose
    [Detailed paragraph defining the core mission - USE the exact Purpose from Canvas 
    and Background from BOSCARD for THIS specific event]
    ### 5-Year Legacy Vision
    [Long-term impact statement based on Canvas Success Vision and BOSCARD Strategic Objectives]
    ### Key Objectives
    [Bulleted summary of top 3 SMART goals - EXTRACT from Canvas Success Metrics and BOSCARD Objectives]

    ## THE PLAN
    ### Scope of Work
    [Paragraph defining the boundaries of THIS project - REFERENCE Canvas Purpose and BOSCARD Scope]
    ### Key Deliverables
    [Bulleted list of tangible outputs - BASED ON BOSCARD Deliverables section and Canvas Experience]
    ### Operational Strategy
    [Paragraph detailing how the team will execute the vision for THIS specific event type]

    ## THE REALITY CHECK
    ### Constraints & Limitations
    [Paragraph detailing budget caps from Canvas Cost Structure, resource limits from Canvas Resources, 
    and hard deadlines from Canvas Plan & Timeline]
    
    ### Risk Register
    [USE THE EXACT 3 RISKS from Canvas Risk & Change section. For each risk:]
    
    **Risk 1: [EXACT name from Canvas]**
    * **Impact:** [High/Med/Low based on Canvas] - [Description from Canvas]
    * **Likelihood:** [EXACT rating from Canvas]
    * **Mitigation:** [Expand the mitigation strategy from Canvas into specific proactive actions]
    * **Owner:** [Project Lead from Canvas or appropriate role]

    **Risk 2: [EXACT name from Canvas]**
    * **Impact:** [From Canvas] - [Description]
    * **Likelihood:** [From Canvas]
    * **Mitigation:** [Specific strategy based on Canvas]
    * **Owner:** [Appropriate role]

    **Risk 3: [EXACT name from Canvas]**
    * **Impact:** [From Canvas] - [Description]
    * **Likelihood:** [From Canvas]
    * **Mitigation:** [Specific strategy based on Canvas]
    * **Owner:** [Appropriate role]

    ## FINANCIAL OVERVIEW
    ### Budget Strategy
    [Paragraph explaining the financial approach - REFERENCE Canvas Cost Structure and Revenue Streams]
    ### Revenue Breakdown
    [Bullet list of specific revenue sources - USE THE EXACT sources and estimates from Canvas Revenue Streams]
    ### Cost Breakdown
    [Bullet list of specific cost centers - USE THE EXACT categories from Canvas Cost Structure]

    ## IMPLEMENTATION ROADMAP
    [Create phases based on the EXACT phases in Canvas Plan & Timeline section]

    ### Phase 1: [Name from Canvas Planning Phase]
    **Focus:** Foundation & Strategy
    [Paragraph overview for THIS event]
    **Key Actions:**
    [Expand the actions from Canvas Planning Phase into 3-5 specific tasks for THIS event type]

    ### Phase 2: [Name from Canvas Promotion Phase]
    **Focus:** Revenue & Awareness
    [Paragraph overview for THIS event]
    **Key Actions:**
    [Expand the actions from Canvas Promotion Phase into 3-5 specific tasks for THIS event]

    ### Phase 3: [Name from Canvas Execution Phase]
    **Focus:** Logistics & Readiness
    [Paragraph overview for THIS event]
    **Key Actions:**
    [Expand the actions from Canvas Execution Phase into 3-5 specific tasks for THIS event type]

    ### Phase 4: Delivery (Event Days)
    **Focus:** Execution & Experience
    [Paragraph overview specific to THIS event from Canvas Experience section]
    **Key Actions:**
    [3-5 critical day-of actions specific to THIS event type]

    ### Phase 5: Legacy (Post-Event)
    **Focus:** Review & Retention
    [Paragraph overview]
    **Key Actions:**
    * Financial Reconciliation
    * Feedback Surveys
    * Planning for next iteration
    
    REMEMBER: Every detail in this Blueprint must align with the SAME event described in Canvas and BOSCARD.
    Use the exact same event type, participant numbers, revenue targets, costs, risks, and timeline phases.
    DO NOT invent a different event.
  `
};

// --- 3. Types ---
type DocStatus = 'idle' | 'pending' | 'complete' | 'error';
type DocType = 'canvas' | 'boscard' | 'blueprint';

interface AppState {
  step: number;
  prompt: string;
  docs: {
    canvas: string;
    boscard: string;
    blueprint: string;
  };
  status: {
    canvas: DocStatus;
    boscard: DocStatus;
    blueprint: DocStatus;
  };
  error: string | null;
}

// --- 4. Utilities ---

const sanitizeOutput = (text: string): string => {
  if (!text) return '';
  return text.replace(/^\|?[-:| ]+\|?$/gm, '');
};

const renderMarkdown = (markdown: string): ReactElement => {
  if (!markdown) return <></>;
  const lines = markdown.split('\n');
  const elements: ReactElement[] = [];
  let currentList: string[] = [];
  let key = 0;

  const parseInline = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*|\*.*?\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} className="font-bold text-slate-900">{part.slice(2, -2)}</strong>;
      if (part.startsWith('*') && part.endsWith('*')) return <em key={i} className="italic text-slate-600">{part.slice(1, -1)}</em>;
      return part;
    });
  };

  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ul key={`list-${key++}`} className="list-disc pl-6 mb-4 space-y-2 marker:text-blue-500">
          {currentList.map((item, idx) => <li key={idx} className="text-slate-700 leading-relaxed pl-1">{parseInline(item)}</li>)}
        </ul>
      );
      currentList = [];
    }
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) { flushList(); return; }
    
    if (trimmed.startsWith('#### ')) { flushList(); elements.push(<h4 key={key++} className="text-sm font-bold text-slate-500 mt-6 mb-2 uppercase tracking-wider">{trimmed.substring(5)}</h4>); return; }
    if (trimmed.startsWith('### ')) { flushList(); elements.push(<h3 key={key++} className="text-lg font-bold text-indigo-700 mt-6 mb-2 flex items-center gap-2"><ChevronRight size={16} className="text-indigo-400"/>{trimmed.substring(4)}</h3>); return; }
    if (trimmed.startsWith('## ')) { flushList(); elements.push(<div key={key++} className="mt-10 mb-4 pb-2 border-b border-slate-200"><h2 className="text-2xl font-extrabold text-slate-900">{trimmed.substring(3)}</h2></div>); return; }
    if (trimmed.startsWith('# ')) { flushList(); elements.push(<h1 key={key++} className="text-4xl font-black text-slate-900 mb-8 border-b-4 border-blue-600 pb-6">{trimmed.substring(2)}</h1>); return; }
    if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) { currentList.push(trimmed.substring(2)); return; }
    
    flushList();
    elements.push(<p key={key++} className="text-slate-700 mb-4 leading-relaxed text-base">{parseInline(trimmed)}</p>);
  });
  flushList();
  return <>{elements}</>;
};

const downloadHTML = (content: string, filename: string, colorHex: string) => {
  try {
    const html = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${filename}</title>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap" rel="stylesheet">
        <style>
          body { font-family: 'Inter', sans-serif; line-height: 1.7; color: #334155; max-width: 900px; margin: 0 auto; padding: 4rem 2rem; background-color: #ffffff; }
          h1 { font-size: 2.5rem; font-weight: 900; color: #0f172a; border-bottom: 6px solid ${colorHex}; padding-bottom: 1.5rem; margin-bottom: 3rem; }
          h2 { font-size: 1.75rem; font-weight: 800; color: #1e293b; margin-top: 3.5rem; margin-bottom: 1rem; border-bottom: 1px solid #e2e8f0; padding-bottom: 0.5rem; }
          h3 { font-size: 1.25rem; font-weight: 700; color: ${colorHex}; margin-top: 2rem; margin-bottom: 0.75rem; }
          h4 { font-size: 0.875rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 1.5rem; }
          p { margin-bottom: 1.25rem; font-size: 1.05rem; }
          ul { margin-bottom: 1.5rem; padding-left: 1.5rem; }
          li { margin-bottom: 0.5rem; }
          strong { color: #0f172a; font-weight: 700; }
          .footer { margin-top: 4rem; padding-top: 2rem; border-top: 1px solid #e2e8f0; text-align: center; font-size: 0.875rem; color: #94a3b8; }
        </style>
      </head>
      <body>
        ${content
          .replace(/\n/g, '\n')
          .split('\n').map(line => {
            const t = line.trim();
            if(!t) return '';
            if(t.startsWith('# ')) return `<h1>${t.substring(2)}</h1>`;
            if(t.startsWith('## ')) return `<h2>${t.substring(3)}</h2>`;
            if(t.startsWith('### ')) return `<h3>${t.substring(4)}</h3>`;
            if(t.startsWith('#### ')) return `<h4>${t.substring(5)}</h4>`;
            if(t.startsWith('* ') || t.startsWith('- ')) return `<li>${t.substring(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}</li>`;
            return `<p>${t.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}</p>`;
          }).join('\n')
          .replace(/((<li>.*<\/li>\n)+)/g, '<ul>$1</ul>')
        }
        <div class="footer">Generated by Event Playbook AI • Powered by Google Gemini 2.5</div>
      </body>
      </html>
    `;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.html`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) { 
    window.alert("Download failed. Please try again."); 
  }
};

// --- 5. API Logic (Robust Fallback) ---
const generateContent = async (prompt: string, retryCount = 0, modelIndex = 0): Promise<string> => {
  if (!GEMINI_API_KEY) throw new Error("Missing API Key. Please check your .env file.");
  
  if (modelIndex >= API_CONFIG.models.length) throw new Error("All services are currently busy. Please wait 30 seconds and try again.");

  const currentModel = API_CONFIG.models[modelIndex];
  
  try {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), 60000); // 60s timeout
    
    const res = await fetch(`${API_CONFIG.endpoint}${currentModel}:generateContent?key=${GEMINI_API_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] }),
      signal: controller.signal
    });
    clearTimeout(id);

    if (res.status === 429 || res.status === 503) {
      if (retryCount < 1) {
        await new Promise(r => setTimeout(r, 2000));
        return generateContent(prompt, retryCount + 1, modelIndex);
      }
      console.warn(`Model ${currentModel} busy, switching...`);
      return generateContent(prompt, 0, modelIndex + 1);
    }

    if (!res.ok) {
      if (res.status === 404 || res.status === 400) {
         return generateContent(prompt, 0, modelIndex + 1);
      }
      throw new Error(`API Error (${res.status})`);
    }
    
    const data = await res.json();
    const rawText = data.candidates?.[0]?.content?.parts?.[0]?.text || "";
    
    if (!rawText) throw new Error("Empty response received.");
    
    return sanitizeOutput(rawText);

  } catch (err) {
    if (modelIndex < API_CONFIG.models.length - 1) {
       return generateContent(prompt, 0, modelIndex + 1);
    }
    throw err;
  }
};

// --- 6. Main Component ---
export default function App() {
  const [state, setState] = useState<AppState>({
    step: 0,
    prompt: '',
    docs: { canvas: '', boscard: '', blueprint: '' },
    status: { canvas: 'idle', boscard: 'idle', blueprint: 'idle' },
    error: null
  });
  
  const [activeTab, setActiveTab] = useState<DocType>('canvas');
  const [copied, setCopied] = useState(false);

  // CHANGED: Updated localStorage key to force fresh start with fixed context
  useEffect(() => {
    try {
      const saved = localStorage.getItem('eventPlaybook_contextFixed_v16');
      if (saved) {
        const data = JSON.parse(saved);
        if (data.status.canvas === 'pending' || data.status.boscard === 'pending' || data.status.blueprint === 'pending') {
           data.status = { canvas: 'idle', boscard: 'idle', blueprint: 'idle' };
           data.error = "Session restored. Please try generating again.";
        }
        setState(data);
      }
    } catch (e) { localStorage.removeItem('eventPlaybook_contextFixed_v16'); }
  }, []);

  useEffect(() => {
    localStorage.setItem('eventPlaybook_contextFixed_v16', JSON.stringify(state));
  }, [state]);

  const handleGenerate = async () => {
    if (state.prompt.trim().split(/\s+/).length < 5) {
      setState(s => ({ ...s, error: "Please provide a more detailed description (at least 5 words)." }));
      return;
    }

    setState(s => ({ ...s, error: null, status: { canvas: 'pending', boscard: 'idle', blueprint: 'idle' } }));

    try {
      // Step 1: Canvas
      console.log('Step 1: Generating Canvas...');
      const canvasText = await generateContent(PROMPTS.canvas(state.prompt));
      setState(s => ({ 
        ...s, 
        docs: { ...s.docs, canvas: canvasText },
        status: { ...s.status, canvas: 'complete', boscard: 'pending' }
      }));

      // Step 2: BOSCARD (with FULL Canvas context)
      console.log('Step 2: Generating BOSCARD with full Canvas context...');
      const boscardText = await generateContent(PROMPTS.boscard(canvasText));
      setState(s => ({ 
        ...s, 
        docs: { ...s.docs, boscard: boscardText },
        status: { ...s.status, boscard: 'complete', blueprint: 'pending' }
      }));

      // Step 3: Blueprint (with FULL Canvas + BOSCARD context)
      console.log('Step 3: Generating Blueprint with full Canvas + BOSCARD context...');
      const blueprintText = await generateContent(PROMPTS.blueprint(canvasText, boscardText));
      setState(s => ({ 
        ...s, 
        step: 1,
        docs: { ...s.docs, blueprint: blueprintText },
        status: { ...s.status, blueprint: 'complete' }
      }));
      
      setActiveTab('canvas');

    } catch (err) {
      setState(s => ({ 
        ...s, 
        status: { canvas: 'error', boscard: 'error', blueprint: 'error' },
        error: err instanceof Error ? err.message : "Generation failed. Please check your API key."
      }));
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const reset = () => {
    if (window.confirm("Start a new event plan? Current progress will be lost.")) {
      setState({
        step: 0, prompt: '', docs: { canvas: '', boscard: '', blueprint: '' },
        status: { canvas: 'idle', boscard: 'idle', blueprint: 'idle' }, error: null
      });
    }
  };

  const forceReset = () => {
      localStorage.removeItem('eventPlaybook_contextFixed_v16');
      window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 font-sans text-slate-800">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-indigo-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-2.5 rounded-xl shadow-lg shadow-blue-200 text-white">
              <Sparkles size={20} strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight leading-none">Event Playbook AI</h1>
              <p className="text-xs text-indigo-600 font-bold mt-0.5 tracking-wide uppercase">Professional Edition</p>
            </div>
          </div>
          <div className="flex gap-2">
            {state.error && (
                <button onClick={forceReset} className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all">
                    <Trash2 size={14} /> Reboot App
                </button>
            )}
            {state.step > 0 && (
                <button onClick={reset} className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-900 rounded-lg transition-all">
                <Home size={16} /> Start Over
                </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-12">
        {state.step === 0 ? (
          // --- INPUT MODE ---
          <div className="max-w-3xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="text-center space-y-4 mb-10">
              <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight">
                Bring Your Event Vision to <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Life</span>
              </h2>
              <p className="text-lg text-slate-600 max-w-xl mx-auto">
                Describe your idea and get a complete strategic plan: Canvas, BOSCARD, and Blueprint—generated in minutes.
              </p>
            </div>

            {/* Inspiration Section */}
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-4 text-amber-600">
                <Lightbulb size={20} />
                <h3 className="font-bold text-slate-900">Need Inspiration? Try these:</h3>
              </div>
              <div className="space-y-3">
                {EXAMPLE_PROMPTS.map((ex, i) => (
                  <button 
                    key={i} 
                    onClick={() => setState(s => ({...s, prompt: ex}))}
                    className="w-full text-left p-3 text-sm text-slate-600 bg-slate-50 hover:bg-blue-50 hover:text-blue-700 rounded-lg border border-slate-100 hover:border-blue-200 transition-all"
                  >
                    <span className="font-bold mr-2">Example {i+1}:</span> {ex.substring(0, 80)}...
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-2xl shadow-indigo-100 border border-slate-200 space-y-6">
              <div>
                <label className="block text-xs font-bold text-indigo-600 mb-2 uppercase tracking-wide">Describe Your Event</label>
                <textarea 
                  value={state.prompt}
                  onChange={e => setState(s => ({ ...s, prompt: e.target.value }))}
                  placeholder="e.g., A 3-day youth basketball tournament for ages 12-16 in Melbourne..."
                  className="w-full h-40 p-4 rounded-xl bg-slate-50 border border-slate-200 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none text-slate-700 text-lg placeholder:text-slate-400 transition-all"
                />
              </div>

              {(state.status.canvas !== 'idle' || state.error) && (
                <div className="bg-slate-50 rounded-xl p-4 space-y-3 border border-slate-100">
                  <StatusRow label="1. Generating Strategy Canvas" status={state.status.canvas} />
                  <StatusRow label="2. Aligning Strategic BOSCARD" status={state.status.boscard} />
                  <StatusRow label="3. Drafting Execution Blueprint" status={state.status.blueprint} />
                </div>
              )}

              {state.error && (
                <div className="flex items-start gap-3 p-4 bg-red-50 text-red-700 rounded-xl border border-red-100 animate-in fade-in">
                  <AlertTriangle size={20} className="shrink-0 mt-0.5" />
                  <p className="text-sm font-medium leading-relaxed">{state.error}</p>
                </div>
              )}

              <button
                onClick={handleGenerate}
                disabled={!state.prompt.trim() || state.status.canvas === 'pending'}
                className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-slate-300 disabled:to-slate-400 disabled:cursor-not-allowed text-white rounded-xl font-bold text-lg shadow-lg shadow-blue-200 hover:shadow-xl hover:shadow-blue-300 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-3"
              >
                {state.status.canvas === 'pending' ? <Loader2 className="animate-spin" /> : <Zap fill="white" className="text-white" />}
                {state.status.canvas === 'pending' ? 'Developing Strategy...' : 'Generate Full Playbook'}
              </button>
            </div>
          </div>
        ) : (
          // --- RESULT MODE ---
          <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6 bg-white p-6 rounded-2xl shadow-lg border border-indigo-100">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 text-green-600 rounded-full shadow-inner">
                  <CheckCircle size={32} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Success!</h2>
                  <p className="text-slate-500">Your professional event playbook is ready.</p>
                </div>
              </div>
              <div className="flex gap-3 w-full md:w-auto">
                <DownloadCard label="Canvas" icon={FileText} color="blue" onClick={() => downloadHTML(state.docs.canvas, 'Event-Canvas', '#2563eb')} />
                <DownloadCard label="BOSCARD" icon={Target} color="purple" onClick={() => downloadHTML(state.docs.boscard, 'BOSCARD-Strategy', '#9333ea')} />
                <DownloadCard label="Blueprint" icon={BookOpen} color="emerald" onClick={() => downloadHTML(state.docs.blueprint, 'Event-Blueprint', '#059669')} />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden min-h-[800px] flex flex-col">
              <div className="flex border-b border-slate-200 bg-slate-50/80 backdrop-blur sticky top-0 z-10">
                <TabButton active={activeTab === 'canvas'} label="1. Canvas" icon={FileText} onClick={() => setActiveTab('canvas')} />
                <TabButton active={activeTab === 'boscard'} label="2. BOSCARD" icon={Target} onClick={() => setActiveTab('boscard')} />
                <TabButton active={activeTab === 'blueprint'} label="3. Blueprint" icon={BookOpen} onClick={() => setActiveTab('blueprint')} />
                
                <div className="ml-auto p-2 flex items-center">
                  <button 
                    onClick={() => copyToClipboard(state.docs[activeTab])}
                    className="flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? 'Copied' : 'Copy Text'}
                  </button>
                </div>
              </div>

              <div className="flex-1 p-8 md:p-16 bg-white overflow-y-auto">
                <div className="max-w-3xl mx-auto">
                  <div className="prose prose-slate prose-lg max-w-none">
                    {renderMarkdown(state.docs[activeTab])}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center">
               <button onClick={reset} className="px-6 py-3 text-slate-500 hover:text-slate-800 font-medium transition-colors flex items-center gap-2 mx-auto">
                 <RefreshCw size={16} /> Create Another Event Plan
               </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// --- Subcomponents ---

const StatusRow = ({ label, status }: { label: string, status: DocStatus }) => (
  <div className="flex items-center gap-3 text-sm">
    <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-500 ${
      status === 'complete' ? 'bg-green-500 text-white scale-100' : 
      status === 'pending' ? 'bg-blue-100 text-blue-600 scale-110' : 
      status === 'error' ? 'bg-red-100 text-red-600' : 
      'bg-slate-100 text-slate-300 scale-90'
    }`}>
      {status === 'complete' ? <Check size={14} strokeWidth={3} /> : status === 'pending' ? <Loader2 size={14} className="animate-spin" /> : status === 'error' ? <AlertTriangle size={14} /> : <div className="w-2 h-2 bg-slate-300 rounded-full" />}
    </div>
    <span className={`font-medium transition-colors duration-300 ${
      status === 'pending' ? 'text-blue-700' : 
      status === 'complete' ? 'text-slate-700' : 
      status === 'error' ? 'text-red-600' : 
      'text-slate-400'
    }`}>
      {label}
    </span>
  </div>
);

const DownloadCard = ({ label, icon: Icon, color, onClick }: any) => {
  const colors: any = {
    blue: 'bg-blue-50 border-blue-100 text-blue-700 hover:border-blue-300 hover:bg-blue-100',
    purple: 'bg-purple-50 border-purple-100 text-purple-700 hover:border-purple-300 hover:bg-purple-100',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100'
  };

  return (
    <button 
      onClick={onClick}
      className={`flex flex-1 md:flex-none flex-col items-center justify-center p-4 min-w-[100px] rounded-xl border transition-all hover:-translate-y-1 hover:shadow-md group ${colors[color]}`}
    >
      <Icon size={24} className="mb-2 opacity-80 group-hover:scale-110 transition-transform" />
      <span className="text-xs font-bold uppercase tracking-wide">{label}</span>
      <div className="flex items-center gap-1 mt-1 opacity-60 text-[10px] font-medium">
        <Download size={10} /> HTML
      </div>
    </button>
  );
};

const TabButton = ({ active, label, icon: Icon, onClick }: any) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-6 py-4 text-sm font-bold transition-all border-b-2 ${
      active 
        ? 'border-blue-600 text-blue-700 bg-white' 
        : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
    }`}
  >
    <Icon size={16} className={active ? 'text-blue-600' : 'text-slate-400'} />
    {label}
  </button>
);
